import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {PLANT_MATERIAL_CONTROL_ITEM_MODEL, buildEntryAndExitFormItem} from "../../Model/Model";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 */
const MaterialsInOutApp = ({
    materialRequest,
    setMaterialRequest,
    setEmptyFieldsItems
}) => {
    const { t } = useTranslation();
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedLevelId, setSelectedLevelId] = useState(null);

    /**
     * Handles changes for dynamically created material request items.
     *
     * Updates the corresponding item field value based on the provided index.
     *
     * @param {Object} e - Input change event.
     * @param {number} index - Index of the item being modified.
     *
     * @returns {void}
     */
    const handleChangeTemp = (e, index) => {
        const { name, value } = e.target;

        setMaterialRequest((prev) => {
            const items = [...prev.items];

            items[index] = {
                ...items[index],
                [name]: value,
            };

            return {
                ...prev,
                items,
            };
        });
    };

    /**
     * Sets the movement type of an item.
     *
     * The type decides whether the material comes into the plant or leaves it,
     * and it is selected from the tabs rendered on top of every item.
     *
     * @param {string} type - Selected movement type of the item.
     * @param {number} index - Index of the item being modified.
     *
     * @returns {void}
     */
    const handleSelectItemType = (type, index) => {
        handleChangeTemp({ target: { name: 'type', value: type } }, index);
    };

    /**
     * Adds a new empty item to the material request.
     *
     * Before adding a new item, validates that all existing items
     * contain the required information.
     *
     * If validation fails:
     * - Displays item validation errors.
     *
     * If validation succeeds:
     * - Appends a new item initialized from GENERAL_ENTRY_EXIT_ITEM_MODEL.
     *
     * @returns {void}
     */
    const handleAdd = () => {
        // The list can be left with no material at all, but it never grows with
        // one that is still waiting to be written, so every material already
        // captured has to be complete before another one is added
        const isComplete = (materialRequest?.items || []).every((e) => e.isValid());

        if(!isComplete){
            setEmptyFieldsItems(true);
            return;
        }
        setEmptyFieldsItems(false);
        setMaterialRequest(prev => ({
            ...prev,
            items: [...prev.items, buildEntryAndExitFormItem(PLANT_MATERIAL_CONTROL_ITEM_MODEL)]
        }));
    };

    /**
     * Removes an item from the material request item list.
     *
     * Displays a success notification after deletion.
     *
     * @param {Object} item - Item to remove from the request.
     *
     * @returns {void}
     */
    const handleDeleteItem = (item) => {
        setMaterialRequest((prev) => ({
            ...prev,
            items: prev.items.filter((currentItem) => currentItem.id !== item.id),
        }));
        toast.success(t('has_been_deleted_successfully'));
    };

    return {
        t,
        handleChangeTemp,
        handleSelectItemType,
        showDeleteModal,
        setShowDeleteModal,
        selectedLevelId,
        setSelectedLevelId,
        handleAdd,
        handleDeleteItem,
    };
};

export default MaterialsInOutApp;
